import mapboxgl from 'mapbox-gl';

/** Mapbox GL draws the whole world as a square this many pixels wide at zoom 0. */
const WORLD_SIZE_AT_ZOOM_0 = 512;

/** `[minLng, minLat, maxLng, maxLat]` */
export type BBox = [number, number, number, number];

/**
 * The lowest zoom at which a `width` × `height` viewport fits inside `bbox`, so the area covers the
 * viewport and leaves nothing outside it to pan to. Fitting the area inside the viewport, as
 * `fitBounds` does, is the opposite: its leftover margin is room to drag out of the area.
 *
 * Null when there is nothing to fit: an empty viewport, or an area with no width or height. Not capped:
 * a small enough area asks for more than the map's max zoom, which only the map knows.
 */
export function coverZoom(bbox: BBox, width: number, height: number): number | null {
  if (!width || !height) return null;

  const [minLng, minLat, maxLng, maxLat] = bbox;
  const southWest = mapboxgl.MercatorCoordinate.fromLngLat({ lng: minLng, lat: minLat });
  const northEast = mapboxgl.MercatorCoordinate.fromLngLat({ lng: maxLng, lat: maxLat });
  const xSpan = Math.abs(northEast.x - southWest.x);
  const ySpan = Math.abs(northEast.y - southWest.y);
  if (!xSpan || !ySpan) return null;

  const zoomX = Math.log2(width / (WORLD_SIZE_AT_ZOOM_0 * xSpan));
  const zoomY = Math.log2(height / (WORLD_SIZE_AT_ZOOM_0 * ySpan));
  return Math.max(zoomX, zoomY);
}
